import React, { forwardRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import api from "../../../config/URL";
import { toast } from "react-toastify";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { CiEdit } from "react-icons/ci";
import PhoneInput from "react-phone-input-2";

const validationSchema = Yup.object().shape({
  parentName: Yup.string().required("*Guardian Name is required"),
  parentDateOfBirth: Yup.date()
    .required("*Date Of Birth is required")
    .max(new Date(), "*Date Of Birth cannot be in the future"),
  email: Yup.string()
    .email("*Invalid email address")
    .required("*Email is required"),
  relation: Yup.string().required("*Relation is required"),
  occupation: Yup.string().required("*Occupation is required"),
  mobileNumber: Yup.string()
    .matches(
      /^\+\d{8,15}$/,
      "*Invalid Phone Number (8-15 digits after country code)"
    )
    .required("*Phone Number is required"),
  postalCode: Yup.string()
    .matches(/^\d{6}$/, "*Postal code must be 6 digits")
    .required("*Postal code is required"),
  address: Yup.string().required("*Address is required"),
  primaryContact: Yup.boolean(),
  files: Yup.mixed()
    .notRequired()
    .test(
      "max-file-name-length",
      "*File name must be at most 50 characters",
      (value) => !value || (value.name && value.name.length <= 50)
    ),
});

const EditParentDetailModel = forwardRef(({ id, getData }) => {
  const [show, setShow] = useState(false);
  const [data, setData] = useState({});
  const [loadIndicator, setLoadIndicator] = useState(false);
  const userName = localStorage.getItem("userName");

  const handleClose = () => {
    setShow(false);
    formik.resetForm();
  };

  const handleShow = async () => {
    setShow(true);
    setLoadIndicator(true);
    try {
      const response = await api.get(`/getAllStudentParentsDetailsById/${id}`);
      const getFormData = {
        ...response.data,
        parentDateOfBirth:
          response.data.parentDateOfBirth?.substring(0, 10) || "",
        primaryContact: response.data.primaryContact || false,
      };
      formik.setValues(getFormData);
      setData(response.data);
    } catch (error) {
      toast.error(
        "Error fetching data: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setLoadIndicator(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      parentName: "",
      parentDateOfBirth: "",
      email: "",
      relation: "",
      occupation: "",
      file: null,
      mobileNumber: "",
      postalCode: "",
      address: "",
      primaryContact: false,
    },
    validationSchema: validationSchema,
    onSubmit: async (data) => {
      setLoadIndicator(true);
      try {
        const formData = new FormData();
        formData.append("parentName", data.parentName);
        formData.append("parentDateOfBirth", data.parentDateOfBirth);
        formData.append("email", data.email);
        formData.append("relation", data.relation);
        formData.append("occupation", data.occupation);
        if (data.file) formData.append("file", data.file);
        formData.append("mobileNumber", data.mobileNumber);
        formData.append("postalCode", data.postalCode);
        formData.append("address", data.address);
        formData.append("primaryContact", data.primaryContact);
        formData.append("updatedBy", userName);

        const response = await api.put(
          `/updateStudentParentsDetailsWithProfileImages/${id}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
        toast.success(response.data.message);
        handleClose();
        getData();
      } catch (error) {
        toast.error(
          "Error updating parent details: " +
            (error.response?.data?.message || error.message)
        );
      } finally {
        setLoadIndicator(false);
      }
    },
  });

  return (
    <div className="container-fluid">
      <button className="btn" type="button" onClick={handleShow}>
        <CiEdit />
      </button>
      <Modal
        show={show}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        onHide={handleClose}
      >
        <form onSubmit={formik.handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              <p className="headColor">Edit Parent/Guardian Detail</p>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {loadIndicator && (
              <div className="text-center mb-3">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}
            <div className="row">
              <div className="col-md-6 col-12 mb-3">
                <label className="form-label">
                  Parents / Guardian Name<span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="parentName"
                  className={`form-control ${
                    formik.touched.parentName && formik.errors.parentName
                      ? "is-invalid"
                      : ""
                  }`}
                  {...formik.getFieldProps("parentName")}
                />
                {formik.touched.parentName && formik.errors.parentName && (
                  <div className="invalid-feedback">
                    {formik.errors.parentName}
                  </div>
                )}
              </div>
              <div className="col-md-6 col-12 mb-3">
                <label className="form-label">
                  Occupation<span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="occupation"
                  className={`form-control ${
                    formik.touched.occupation && formik.errors.occupation
                      ? "is-invalid"
                      : ""
                  }`}
                  {...formik.getFieldProps("occupation")}
                />
                {formik.touched.occupation && formik.errors.occupation && (
                  <div className="invalid-feedback">
                    {formik.errors.occupation}
                  </div>
                )}
              </div>
              <div className="col-md-6 col-12 mb-3">
                <label className="form-label">
                  Date Of Birth<span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  name="parentDateOfBirth"
                  className={`form-control ${
                    formik.touched.parentDateOfBirth &&
                    formik.errors.parentDateOfBirth
                      ? "is-invalid"
                      : ""
                  }`}
                  {...formik.getFieldProps("parentDateOfBirth")}
                />
                {formik.touched.parentDateOfBirth &&
                  formik.errors.parentDateOfBirth && (
                    <div className="invalid-feedback">
                      {formik.errors.parentDateOfBirth}
                    </div>
                  )}
              </div>
              <div className="col-md-6 col-12 mb-3">
                <label className="form-label">Profile Image</label>
                <input
                  type="file"
                  name="file"
                  className="form-control"
                  onChange={(event) =>
                    formik.setFieldValue("file", event.target.files[0])
                  }
                  accept=".jpg,.jpeg,.png,.gif,.bmp"
                />
                {formik.touched.file && formik.errors.file && (
                  <div className="invalid-feedback">{formik.errors.file}</div>
                )}
                <div className="mt-2">
                  {data.profileImage ? (
                    <img
                      src={data.profileImage}
                      className="img-fluid rounded"
                      style={{
                        width: "100px",
                        height: "100px",
                        objectFit: "cover",
                      }}
                      alt="Profile"
                      onError={(e) =>
                        (e.target.src = "/path/to/fallback-image.jpg")
                      }
                    />
                  ) : (
                    <div
                      style={{
                        width: "100px",
                        height: "100px",
                        backgroundColor: "#e0e0e0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      No Image
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-6 col-12 mb-3">
                <label className="form-label">
                  Email<span className="text-danger">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  className={`form-control ${
                    formik.touched.email && formik.errors.email
                      ? "is-invalid"
                      : ""
                  }`}
                  {...formik.getFieldProps("email")}
                />
                {formik.touched.email && formik.errors.email && (
                  <div className="invalid-feedback">{formik.errors.email}</div>
                )}
              </div>
              <div className="col-md-6 col-12 mb-3">
                <label className="form-label">
                  Mobile No<span className="text-danger">*</span>
                </label>
                <PhoneInput
                  country={"sg"}
                  enableSearch={true}
                  inputProps={{
                    name: "mobileNumber",
                    required: true,
                    onBlur: formik.handleBlur,
                  }}
                  value={formik.values.mobileNumber}
                  onChange={(phone) =>
                    formik.setFieldValue("mobileNumber", "+" + phone)
                  }
                  containerClass="phone-input-container"
                  inputClass="phone-input"
                  buttonClass="phone-flag-button"
                />
                {formik.touched.mobileNumber && formik.errors.mobileNumber && (
                  <div className="invalid-feedback">
                    {formik.errors.mobileNumber}
                  </div>
                )}
              </div>
              <div className="col-md-6 col-12 mb-3">
                <label className="form-label">
                  Relation<span className="text-danger">*</span>
                </label>
                <select
                  name="relation"
                  className={`form-select ${
                    formik.touched.relation && formik.errors.relation
                      ? "is-invalid"
                      : ""
                  }`}
                  {...formik.getFieldProps("relation")}
                >
                  <option value="">Select Relation</option>
                  <option value="Brother">Brother</option>
                  <option value="Father">Father</option>
                  <option value="Mother">Mother</option>
                  <option value="Sister">Sister</option>
                </select>
                {formik.touched.relation && formik.errors.relation && (
                  <div className="invalid-feedback">
                    {formik.errors.relation}
                  </div>
                )}
              </div>
              <div className="col-md-6 col-12 mb-3">
                <label className="form-label">
                  Postal Code<span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="postalCode"
                  className={`form-control ${
                    formik.touched.postalCode && formik.errors.postalCode
                      ? "is-invalid"
                      : ""
                  }`}
                  {...formik.getFieldProps("postalCode")}
                />
                {formik.touched.postalCode && formik.errors.postalCode && (
                  <div className="invalid-feedback">
                    {formik.errors.postalCode}
                  </div>
                )}
              </div>
              <div className="col-md-12 col-12 mb-3">
                <label className="form-label">
                  Address<span className="text-danger">*</span>
                </label>
                <textarea
                  name="address"
                  className={`form-control ${
                    formik.touched.address && formik.errors.address
                      ? "is-invalid"
                      : ""
                  }`}
                  {...formik.getFieldProps("address")}
                />
                {formik.touched.address && formik.errors.address && (
                  <div className="invalid-feedback">
                    {formik.errors.address}
                  </div>
                )}
              </div>
              <div className="col-md-12 col-12 mb-3 d-none">
                <label className="form-label">Primary Contact</label>
                <input
                  type="checkbox"
                  name="primaryContact"
                  checked={formik.values.primaryContact}
                  onChange={formik.handleChange}
                  className="form-check-input ms-2"
                />
              </div>
            </div>
          </Modal.Body>
          {/* <Modal.Footer className="mt-3">
            <Button variant="secondary" size="sm" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={loadIndicator}
            >
              {loadIndicator && (
                <span className="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
              )}
              Update
            </Button>
          </Modal.Footer> */}
          <Modal.Footer className="mt-3">
            <Button
              className="btn btn-sm btn-border bg-light text-dark"
              onClick={handleClose}
            >
              Cancel
            </Button>

            <button
              type="submit"
              onSubmit={formik.handleSubmit}
              className="btn btn-button btn-sm"
              disabled={loadIndicator}
            >
              {loadIndicator && (
                <span
                  className="spinner-border spinner-border-sm me-2"
                  aria-hidden="true"
                ></span>
              )}
              Update
            </button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
});

export default EditParentDetailModel;
